import { createFileRoute } from '@tanstack/react-router';
import { ContentHeader, UseCaseCard, UseCaseGrid, CTASection } from "@/components/content";

export const Route = createFileRoute('/use-cases/')({
  component: UseCasesPage,
  head: () => ({
    meta: [
      { title: 'Use Cases | Matricks' },
      { name: 'description', content: 'Discover how Matricks solves real business challenges: API management, FinOps, data synchronization, AI automation, and enterprise security.' },
    ],
  }),
});

const useCases = [
  {
    title: "API Management",
    description:
      "Centralize control of all your APIs. Monitor, secure, and manage API traffic with enterprise-grade governance.",
    href: "/use-cases/api-management",
    iconName: "Router",
    audience: "Platform teams, API developers",
    benefits: [
      "Unified API gateway for all services",
      "Real-time monitoring and analytics",
      "Security and policy enforcement",
    ],
  },
  {
    title: "FinOps & Cost Optimization",
    description:
      "Track and optimize AI and API spending across your organization. Set budgets, get alerts, and reduce waste.",
    href: "/use-cases/finops",
    iconName: "DollarSign",
    audience: "FinOps, Engineering managers",
    benefits: [
      "Real-time cost tracking by team/project",
      "Budget alerts and spending controls",
      "AI model cost optimization",
    ],
  },
  {
    title: "Data Synchronization",
    description:
      "Keep data consistent across all your systems. Automate data flows between CRM, ERP, databases, and more.",
    href: "/use-cases/data-synchronization",
    iconName: "RefreshCw",
    audience: "Data engineers, IT teams",
    benefits: [
      "Real-time data sync across systems",
      "2000+ pre-built connectors",
      "Conflict resolution and error handling",
    ],
  },
  {
    title: "AI Automation",
    description:
      "Deploy AI agents to automate complex business processes. From customer support to data analysis.",
    href: "/use-cases/ai-automation",
    iconName: "Bot",
    audience: "Product teams, Innovation teams",
    benefits: [
      "No-code AI agent builder",
      "Tool integration for autonomous tasks",
      "Multi-agent orchestration",
    ],
  },
  {
    title: "Enterprise Security",
    description:
      "Implement zero-trust security across all your APIs and integrations. Meet compliance requirements.",
    href: "/use-cases/enterprise-security",
    iconName: "Shield",
    audience: "CISOs, Security teams",
    benefits: [
      "Fine-grained access control",
      "Comprehensive audit logging",
      "SOC2, GDPR, HIPAA compliance",
    ],
  },
];

function UseCasesPage() {
  return (
    <div>
      <ContentHeader
        title="Solutions for Every Challenge"
        description="See how Matricks helps organizations solve real business problems with API integration, AI agents, and enterprise security."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Use Cases" }]}
      />

      <UseCaseGrid columns={2}>
        {useCases.map((useCase) => (
          <UseCaseCard
            key={useCase.href}
            title={useCase.title}
            description={useCase.description}
            href={useCase.href}
            iconName={useCase.iconName}
            audience={useCase.audience}
            benefits={useCase.benefits}
          />
        ))}
      </UseCaseGrid>

      <CTASection
        title="Find Your Solution"
        description="See how Matricks can help solve your integration challenges."
        primaryAction={{ label: "Contact Sales", href: "/contact" }}
        secondaryAction={{ label: "View Pricing", href: "/pricing" }}
        background="muted"
      />
    </div>
  );
}
